import { inject, Injectable, Injector, NgZone, runInInjectionContext } from '@angular/core';
import { AppData } from '../AppData';
import Constants from '../../Constants';
import { SseLogoutEvent } from '../../events/sse/sse-logout-event.model';
import { SseLoginEvent } from '../../events/sse/sse-login-event.model';
import { SseRightsNotificationEvent } from '../../events/sse/sse-rights-notification-event.model';
import { SseChecklistTemplateVersionEvent } from '../../events/sse/sse-checklist-template-version-event.model';
import { SseInvoicePositionEvent } from '../../events/sse/sse-invoice-position-event.model';
import { MessageDesignBUtils } from '../../content/components/message-design-b/message-design-b.utils';
import { GlobalMessageClientService } from '../client/global-message-client-service/global-message-client.service';
import { Router } from '@angular/router';
import { CompoApp } from '../CompoApp';
import { InProgressMessageClientService } from '../client/in-progress-message/in-progress-message.client.service';
import { Store } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { SseObjectEvent } from '../../events/sse/sse-object-event.model';
import { NotificationReasonEnum } from '../../enums/notification/notification-reason.enum';
import { RequestClientService } from '../client/request-client.service';
import { ObjectAction } from '../../states/object/action/object-action.actions';
import { MessageActionButtonTypeEnum, MessageModel } from '../../content/components/message-design-b/message.model';
import { CompogeniusNavi } from '../client/navigation-service/base-routes-navigation-client.service';
import { AuthserviceService } from '../../content/pages-new-design/auth-page/login-page/authservice.service';
import { HeaderClientService } from '../client/header-service/header-client.service';
import { IdentityRequestService } from '../request/identity-request.service';
import { TakenOverIdentity } from '../../models/rights/identity/taken-over-identity.model';
import { SseModuleEvent } from '../../events/sse/sse-module-event.model';
import { SseMultiObjectEvent } from '../../events/sse/sse-multi-object-event.model';
import { SseChecklistTemplateActionEvent } from '../../events/sse/sse-checklist-template-action-event.model';
import { SseImportEvent } from '../../events/sse/sse-import-event.model';
import { SseClassEvent } from '../../events/sse/sse-class-event.model';
import { SseOrganizationEvent } from '../../events/sse/sse-organization-event.model';
import { SseJobEvent } from '../../events/sse/sse-job-event.model';
import { SseMessageEvent } from '../../events/sse/sse-message-event.model';
import { SseAccessKeyEvent } from '../../events/sse/sse-accessKey-event.model';
import { SseCommunityEvent } from '../../events/sse/sse-community-event.model';
import { SseNotificationEventByUserEvent } from '../../events/sse/sse-notification-event-by-user.event';
import { SseTodoEvent } from '../../events/sse/sse-todo-event.model';

/**
 * provided in {@link SingletonModule}
 */
@Injectable()
export class SseSingletonService {

  //region state fields
  private sse: EventSource;
  private reconnectTrySecond: number = 1;
  //endregion

  //region INJECTIONS
  private data = inject(AppData);
  private messageClientService = inject(GlobalMessageClientService);
  private router = inject(Router);
  private injector = inject(Injector);
  private ngZone = inject(NgZone);
  private on = inject(CompoApp).on;
  private inProgressMessageService = inject(InProgressMessageClientService);
  private store = inject(Store);
  private translateService = inject(TranslateService);
  //endregion

  public isConnected(): boolean {
    return this.sse && this.sse.readyState == 1;
  }

  public connect(): void {
    console.dir('connect');
    if (!this.data.getToken()) {
      return;
    }

    try {
      const url = Constants.URLS.sse.validate + '?ACCESS_TOKEN=' + this.data.getToken();
      fetch(url, { method: 'GET' })
        .then(() => {
          if (this.sse) {
            this.sse.close();
            delete(this.sse);
          }
          this.sse = new EventSource(Constants.URLS.sse.register + '?ACCESS_TOKEN=' + this.data.getToken());

          runInInjectionContext(this.injector, () => {
            //region login or logout
            new SseLogoutEvent().addListener(this.sse);
            new SseLoginEvent().addListener(this.sse);
            //endregion

            //region rights
            new SseRightsNotificationEvent().addListener(this.sse);
            //endregion

            //region checklist
            new SseChecklistTemplateVersionEvent().addListener(this.sse);
            new SseChecklistTemplateActionEvent().addListener(this.sse);
            //endregion

            //region object
            new SseObjectEvent().addListener(this.sse);
            new SseMultiObjectEvent().addListener(this.sse);
            //endregion

            //region invoice
            new SseInvoicePositionEvent().addListener(this.sse);
            //endregion

            //region module
            new SseModuleEvent().addListener(this.sse);
            //endregion

            //region import
            new SseImportEvent().addListener(this.sse);
            //endregion

            //region class
            new SseClassEvent().addListener(this.sse);
            //endregion

            //region organization
            new SseOrganizationEvent().addListener(this.sse);
            //endregion

            //region job
            new SseJobEvent().addListener(this.sse);
            //endregion

            //region message
            new SseMessageEvent().addListener(this.sse);
            //endregion

            //region accessKey
            new SseAccessKeyEvent().addListener(this.sse);
            //endregion

            //region community
            new SseCommunityEvent().addListener(this.sse);
            //endregion

            //region notification
            new SseNotificationEventByUserEvent().addListener(this.sse);
            //endregion

            //region to-dos
            new SseTodoEvent().addListener(this.sse);
            //endregion

          });

          this.sse.onmessage = (event) => runInInjectionContext(this.injector, () => {
            const resp = JSON.parse(event.data);

            if (resp.sessionLogin) {
              this.ngZone.run(() => {
                this.router.navigate(['/home']);
              });
            }

            if (resp.events?.length > 0) {
              resp.events.forEach(event => {
                this.ngZone.run(() => {

                  switch (event) {

                    case NotificationReasonEnum.DUE_DATE_FINISHED: {
                      //old implementation
                      //notifications are not important anymore (if there are sent at all)
                      break;
                    }

                  }
                });
              });
            }
          });

          this.calculateReconnectSecond();
          this.sse.onerror = () => setTimeout(() => {
            this.disconnect();
            this.connect();
          }, this.reconnectTrySecond * 1000);
        })
        .catch(err => {
          console.warn(err);
          this.messageClientService.messages$.next(MessageDesignBUtils.cannotReachServer());
          this.data.clear();
          this.router.navigate(['/']);

          setTimeout(() => {
            this.connect();
          }, 3000);
        });
    } catch (e) {
      console.warn(e);
    }
  }

  public disconnect(): void {
    if (this.sse) {
      this.sse.close();
    }
  }

  public unload(): void {
    this.disconnect();
    this.sse = null;
    const url = Constants.URLS.sse.remove + '?ACCESS_TOKEN=' + this.data.getToken();
    fetch(url, { method: 'GET' })
      .then(resp => {
      });
  }

  private calculateReconnectSecond(): void {
    if (this.reconnectTrySecond < 60) {
      this.reconnectTrySecond *= 2;
    }
  }
}
